import { WebshopProduct } from '@cldsystems/ws-api-client';
import {
  getEffectiveCustomerPrice,
  type PriceFilterContext,
} from '@/utils/effective-product-price';
import { sortPriceFilterOptions } from '@/utils/price-interval-sort-key';

export type PriceFilterTranslations = {
  common?: {
    price?: string;
  };
};

/** Number of preset price ranges shown for a category (excluding custom interval). */
export const PRICE_FILTER_BUCKET_COUNT = 5;

/** Fallback range when the category has no products (RON-scale storefront defaults). */
const EMPTY_CATALOG_MIN = 0;
const EMPTY_CATALOG_MAX = 1000;

function partitionRangeIntoBuckets(
  minValue: number,
  maxValue: number,
  bucketCount: number,
  currency: string,
): { label: string; value: string }[] {
  let span = maxValue - minValue;
  if (span <= 0) {
    const pad = 10;
    maxValue = minValue + pad;
    span = pad;
  }

  // Ensure enough spread that five integer buckets can each have positive width.
  if (span < bucketCount) {
    maxValue = minValue + bucketCount;
    span = bucketCount;
  }

  const options: { label: string; value: string }[] = [];

  for (let i = 0; i < bucketCount; i++) {
    const start = minValue + Math.floor((span * i) / bucketCount);
    const end =
      i === bucketCount - 1 ? maxValue : minValue + Math.floor((span * (i + 1)) / bucketCount);

    if (start >= end) {
      continue;
    }

    options.push({
      label: `${start} - ${end} ${currency}`,
      value: `${start}-${end}`,
    });
  }

  return options;
}

export const getPriceFilter = (
  products: WebshopProduct[],
  currency: string,
  t?: PriceFilterTranslations,
  ctx?: PriceFilterContext | null,
) => {
  const base = {
    id: 'price',
    name: t?.common?.price ?? 'Price',
    isInterval: true,
    isSearchField: false,
  } as const;

  if (!products.length) {
    const options = sortPriceFilterOptions(
      partitionRangeIntoBuckets(
        EMPTY_CATALOG_MIN,
        EMPTY_CATALOG_MAX,
        PRICE_FILTER_BUCKET_COUNT,
        currency,
      ),
    );

    return {
      ...base,
      minIntervalValue: EMPTY_CATALOG_MIN,
      maxIntervalValue: EMPTY_CATALOG_MAX,
      options,
    };
  }

  const amounts = products
    .map(product => getEffectiveCustomerPrice(product, ctx))
    .filter(price => typeof price === 'number' && !Number.isNaN(price));

  if (amounts.length === 0) {
    const options = sortPriceFilterOptions(
      partitionRangeIntoBuckets(
        EMPTY_CATALOG_MIN,
        EMPTY_CATALOG_MAX,
        PRICE_FILTER_BUCKET_COUNT,
        currency,
      ),
    );

    return {
      ...base,
      minIntervalValue: EMPTY_CATALOG_MIN,
      maxIntervalValue: EMPTY_CATALOG_MAX,
      options,
    };
  }

  const minP = Math.min(...amounts);
  const maxP = Math.max(...amounts);
  const minValue = Math.floor(minP / 10) * 10;
  const maxValue = Math.ceil(maxP / 10) * 10;

  const options = sortPriceFilterOptions(
    partitionRangeIntoBuckets(minValue, maxValue, PRICE_FILTER_BUCKET_COUNT, currency),
  );

  return {
    ...base,
    minIntervalValue: minValue,
    maxIntervalValue: maxValue,
    options,
  };
};
